import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, ShieldCheck } from "lucide-react";
import api, { fileUrl } from "@/lib/api";

/**
 * Shows the top kitchens most likely to still be free this coming weekend.
 * Renders nothing while loading (keeps the bookings tab quiet) and hides itself
 * gracefully when the API returns no picks.
 */
export function WeekendPicks() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/predict/weekend-free")
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => alive && setData({ picks: [] }));
    return () => { alive = false; };
  }, []);

  if (data === null) return null;
  const picks = data.picks || [];
  if (picks.length === 0) return null;

  const fmt = (iso) => new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

  return (
    <div data-testid="weekend-picks" className="mb-10 border hairline p-6">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="tick-label flex items-center gap-2"><CalendarClock size={13} /> Prediction</div>
          <h2 className="font-display font-bold text-2xl mt-1">Still likely free this weekend.</h2>
        </div>
        <div className="tick-label text-neutral-500">{fmt(data.weekend.sat)} – {fmt(data.weekend.sun)}</div>
      </div>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {picks.slice(0, 6).map((k) => (
          <Link
            key={k.id}
            to={`/kitchen/${k.id}`}
            data-testid={`weekend-pick-${k.id}`}
            className="k-card block"
          >
            <div className="aspect-[4/3] overflow-hidden bg-neutral-900 relative">
              <img
                src={k.photos?.[0] ? fileUrl(k.photos[0]) : "https://images.unsplash.com/photo-1782750161991-23529c9462bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxjb21tZXJjaWFsJTIwa2l0Y2hlbiUyMHN0YWlubGVzcyUyMHN0ZWVsfGVufDB8fHx8MTc4NzM4OTQyMXww&ixlib=rb-4.1.0&q=85"}
                alt={k.name}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {k.host_verified && (
                <div className="absolute top-3 left-3 flex items-center gap-1 bg-[#FF3B30] text-white px-2 py-1 text-[10px] uppercase tracking-widest font-medium">
                  <ShieldCheck size={11} strokeWidth={2.5} /> Verified
                </div>
              )}
              <div className="absolute bottom-3 right-3 bg-black/70 text-white px-2 py-1 font-mono text-xs">
                ~{k.weekend_free_hours}h free
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-baseline justify-between">
                <div className="font-display font-bold text-lg truncate pr-2">{k.name}</div>
                <div className="font-mono text-sm">${k.hourly_rate}/hr</div>
              </div>
              <div className="mt-1 text-neutral-500 text-xs">{k.city}{k.state ? `, ${k.state}` : ""}</div>
              <div className="mt-2 tick-label text-neutral-500">
                {k.weekend_booked_hours > 0 ? `${k.weekend_booked_hours}h booked · ` : ""}
                {k.recent_booking_count === 0 ? "quiet lately" : `${k.recent_booking_count} recent bookings`}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
